// Írás a Firestore-ba.
//
// A szerződést a Flutter `tasks_repo.dart`-ból vettem át, mert a két felület
// UGYANAZT az adatot írja — bármelyik eltérés csendben elrontaná a másikat.
// Két külön útvonal van, és ezeket nem szabad összekeverni:
//
//  1. ÁLLAPOTVÁLTOZÁS (státusz, elfogadás, időmérés, megjegyzés)
//     -> az AKTUÁLIS taskChange dokumentum HELYBEN frissül.
//        (Flutter: acceptTask -> update(taskChanges/{id}, task.toJson()))
//
//  2. SZERKEZETI VÁLTOZÁS (új feladat a projektben, sorrend, cím)
//     -> ÚJ taskChange verzió születik:
//        régi.isCurrent = false
//        új dokumentum: previousTaskChangeId = régi.id, isCurrent = true
//        a projekt dokumentumán currentId = új.id
//        (Flutter: createTask)
//
// A Flutter ezeket egymás utáni külön írásokkal végzi; itt writeBatch-csel,
// tehát atomi módon. Így nem maradhat két `isCurrent: true` dokumentum, se
// olyan projekt, aminek a `currentId`-ja nem létező verzióra mutat.

import {
  collection,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  writeBatch,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore';
import type { TaskStatus } from './model';

type Raw = Record<string, unknown>;

export type WriteContext = {
  /// A kapcsolat kivulrol jon, nem globalis szingleton — igy az irasi reteg
  /// emulator ellen is futtathato, valodi adat erintese nelkul.
  db: Firestore;
  companyId: string;
  projectId: string;
  /// Az aktuális taskChange dokumentum azonosítója.
  changeId: string;
  userId: string;
};

function taskChangeRef(ctx: WriteContext, changeId?: string) {
  return doc(
    ctx.db,
    'projects',
    ctx.companyId,
    'projects',
    ctx.projectId,
    'taskChanges',
    changeId ?? ctx.changeId,
  );
}

function projectRef(ctx: WriteContext) {
  return doc(ctx.db, 'projects', ctx.companyId, 'projects', ctx.projectId);
}

async function loadCurrent(ctx: WriteContext): Promise<DocumentData> {
  const snapshot = await getDoc(taskChangeRef(ctx));
  if (!snapshot.exists()) {
    throw new Error('A feladat időközben megváltozott. Frissítsd az oldalt.');
  }
  return snapshot.data();
}

function tasksOf(change: DocumentData): Raw[] {
  return Array.isArray(change['tasks']) ? ([...change['tasks']] as Raw[]) : [];
}

function indexOfTask(tasks: Raw[], taskId: string): number {
  const index = tasks.findIndex((t) => t['id'] === taskId);
  if (index < 0) throw new Error('A feladat nem található a projektben.');
  return index;
}

/// A futó időmérés lezárása.
///
/// Pontosan a Flutter `setUpNewTimeTrack()` logikája: a lezáratlan szakasz
/// `finished` mezőt kap, és az addig eltelt másodpercek hozzáadódnak a
/// `duration`-höz. Minden státuszváltás ezzel indul.
export function closeOpenTimeTrack(task: Raw, now: Date = new Date()): void {
  const tracks = Array.isArray(task['timeTracks']) ? (task['timeTracks'] as Raw[]) : [];
  const index = tracks.findIndex((t) => t['finished'] === undefined || t['finished'] === null);
  if (index < 0) return;

  const track = { ...tracks[index] } as Raw;
  const startedRaw = track['started'];
  if (typeof startedRaw !== 'string') return;

  const started = new Date(startedRaw);
  const seconds = Math.max(0, Math.round((now.getTime() - started.getTime()) / 1000));

  track['finished'] = now.toISOString();
  track['duration'] = (typeof track['duration'] === 'number' ? track['duration'] : 0) + seconds;

  tracks[index] = track;
  task['timeTracks'] = tracks;
}

/// Állapotváltozás mentése: az aktuális verzió helyben frissül.
/// Ugyanaz, mint az `updateTaskInPlace`, csak kívülről is hívható.
///
/// A csatolmány-feltöltés (`uploads.ts`) ezzel írja vissza a feladatra a
/// Storage-hivatkozást; nem szerkezeti változás, ezért nem születik új verzió.
export async function updateTaskRaw(
  ctx: WriteContext,
  taskId: string,
  mutate: (task: Raw) => void,
): Promise<void> {
  await updateTaskInPlace(ctx, taskId, (task) => mutate(task));
}

async function updateTaskInPlace(
  ctx: WriteContext,
  taskId: string,
  mutate: (task: Raw, change: DocumentData) => void,
): Promise<void> {
  const change = await loadCurrent(ctx);
  const tasks = tasksOf(change);
  const index = indexOfTask(tasks, taskId);

  const task = { ...tasks[index] } as Raw;
  mutate(task, change);
  tasks[index] = task;

  const batch = writeBatch(ctx.db);
  batch.update(taskChangeRef(ctx), {
    tasks,
    updatedAt: new Date().toISOString(),
    modifierUserId: ctx.userId,
  });
  await batch.commit();
}

// ---------------------------------------------------------------------------
// Állapotváltozások
// ---------------------------------------------------------------------------

export async function changeTaskStatus(
  ctx: WriteContext,
  taskId: string,
  input: { status: TaskStatus; reason: string; stopTimer: boolean },
): Promise<void> {
  await updateTaskInPlace(ctx, taskId, (task) => {
    const now = new Date();

    // A Flutter minden statuszvaltasnal lezarja a futo merest; a felhasznalo
    // dontese csak azt befolyasolja, hogy KERTE-e — ha nem, nem nyulunk hozza.
    if (input.stopTimer) closeOpenTimeTrack(task, now);

    task['status'] = input.status;
    task['statusChangeDesc'] = input.reason;

    if (input.status === 'failed') {
      task['failedDesc'] = input.reason;
    } else if (input.status === 'doing' || input.status === 'completed') {
      // A korabbi sikertelenseg oka nem maradhat ott egy ujra elindult
      // feladaton — a Flutter is torli.
      task['failedDesc'] = null;
    }

    if (input.status === 'failed' || input.status === 'completed') {
      task['finished'] = now.toISOString();
    }
  });
}

export async function acceptTask(
  ctx: WriteContext,
  taskId: string,
  input: { accepted: boolean; comment: string },
): Promise<void> {
  await updateTaskInPlace(ctx, taskId, (task) => {
    task['accepted'] = input.accepted;
    if (input.accepted) {
      task['status'] = 'todo';
      task['declineDesc'] = null;
    } else {
      task['status'] = 'unaccepted';
      task['declineDesc'] = input.comment;
    }
  });
}

/// Az időmérés INDÍTÁSA.
///
/// A szerződés a Flutter `tasks_repo.timerTask(..., isStart: true)`-jéből jön,
/// nem találgatásból:
///   - új szakasz kerül a `timeTracks` tömbbe (`started` = most),
///   - ha a feladat MÉG NEM indult el, a `started` mező és a státusz is
///     átáll „folyamatban"-ra.
///
/// Az utóbbi nem mellékhatás, hanem szándék: a mérés megkezdése egyben azt is
/// jelenti, hogy a munka elkezdődött. A mobil pontosan ezt teszi, és ha a web
/// másképp tenné, a két felület ugyanarra a gombra mást írna.
export async function startTaskTimer(ctx: WriteContext, taskId: string): Promise<void> {
  await updateTaskInPlace(ctx, taskId, (task) => {
    const now = new Date();
    const tracks = Array.isArray(task['timeTracks']) ? ([...task['timeTracks']] as Raw[]) : [];

    // Ha valamiert mar fut egy szakasz, NEM nyitunk masodikat. Ket nyitott
    // szakaszbol az `elapsedSecondsOf` duplan szamolna, a leallitas pedig
    // csak az egyiket zarna le — a kulonbseg csendben nonne.
    const running = tracks.some(
      (track) =>
        typeof track['started'] === 'string' &&
        (track['finished'] === undefined || track['finished'] === null),
    );
    if (running) return;

    tracks.push({ started: now.toISOString(), finished: null, duration: null });
    task['timeTracks'] = tracks;

    if (typeof task['started'] !== 'string') {
      task['started'] = now.toISOString();
      task['status'] = 'doing';
    }
  });
}

/// Az időmérés LEÁLLÍTÁSA: a nyitott szakasz lezárása.
///
/// A `duration` másodpercben halmozódik, ahogy a mobil `setUpNewTimeTrack`-je
/// csinálja — nem felülírja, hanem HOZZÁADJA a korábbi értékhez.
export async function stopTaskTimer(ctx: WriteContext, taskId: string): Promise<void> {
  await updateTaskInPlace(ctx, taskId, (task) => {
    closeOpenTimeTrack(task);
  });
}

export async function overrideTaskTime(
  ctx: WriteContext,
  taskId: string,
  input: { seconds: number; reason: string },
): Promise<void> {
  await updateTaskInPlace(ctx, taskId, (task) => {
    // A Flutter a mert ido HELYETT hasznalja a rewriteDuration-t, nem
    // hozzaadja — ezert a mert szakaszokat nem bantjuk.
    closeOpenTimeTrack(task);
    task['rewriteDuration'] = input.seconds;
    task['statusChangeDesc'] = input.reason;
  });
}

export async function reopenTask(
  ctx: WriteContext,
  taskId: string,
  input: { reason: string; deadline?: Date },
): Promise<void> {
  const reason = input.reason;

  await updateTaskInPlace(ctx, taskId, (task) => {
    const reopens = Array.isArray(task['reopens']) ? ([...task['reopens']] as Raw[]) : [];
    reopens.push({
      // A Flutter `TaskReopenModel` mezoneve `reopenDesc`, nem `desc`. A web
      // eddig `desc`-kent irta, igy az indoklas sem a mobilon, sem a webes
      // idovonalon (W06) nem jelent meg — csak ott allt a dokumentumban.
      reopenDesc: reason,
      userId: ctx.userId,
      createdAt: new Date().toISOString(),
    });

    task['reopens'] = reopens;
    task['reopenDesc'] = reason;
    // A terv (W10) uj hataridot is kerhet. Ures mezonel a korabbi marad.
    if (input.deadline) task['deadline'] = input.deadline.toISOString();
    // A Flutter oldal 'todo'-ra allit vissza, nem 'doing'-ra: az ujranyitott
    // feladat elvegzendo, nem automatikusan folyamatban levo. A ket felulet
    // UGYANAZT az adatot irja, ezert itt is ennek kell allnia.
    task['status'] = 'todo';
    // A lezaraskor beirt idopont es a sikertelenseg oka nem maradhat ott egy
    // ujranyitott feladaton — kulonben a lista lezartkent mutatna reszleteit.
    task['finished'] = null;
    task['failedDesc'] = null;
  });
}

export async function addComment(
  ctx: WriteContext,
  taskId: string,
  input: { text: string; mentionedIds: string[] },
): Promise<void> {
  await updateTaskInPlace(ctx, taskId, (task) => {
    const comments = Array.isArray(task['comments']) ? ([...task['comments']] as Raw[]) : [];
    comments.push({
      // A Flutter nanoid-ot hasznal; itt barmilyen egyedi azonosito jo, mert
      // a mezo csak a listan beluli megkulonboztetesre szolgal.
      id: crypto.randomUUID(),
      text: input.text,
      submittedUserId: ctx.userId,
      createdAt: new Date().toISOString(),
      taggedUserIds: input.mentionedIds,
    });
    task['comments'] = comments;
  });
}

// ---------------------------------------------------------------------------
// Szerkezeti változás: új verzió
// ---------------------------------------------------------------------------

export type NewTaskPayload = {
  title: string;
  description: string;
  priority: number;
  deadline?: Date;
  assigneeIds: string[];
  /// A koveto (`followerUserId`). A terv (W07) kulon mezot mutat ra, es a
  /// feladatmodell is tarolja — az uj feladat irasa eddig kihagyta.
  followerUserId?: string;
  /// Az egyedi mezok: a definiciokbol es a bevitt ertekekbol allitjuk ossze.
  fields?: { id: string; name: string; type: string; value: string }[];
};

/// Új feladat a projektbe — ez SZERKEZETI változás, ezért új verzió születik.
export async function addTaskToProject(
  ctx: WriteContext,
  payload: NewTaskPayload,
): Promise<string> {
  const change = await loadCurrent(ctx);
  const tasks = tasksOf(change);

  const newTask: Raw = {
    id: crypto.randomUUID(),
    title: payload.title,
    desc: payload.description,
    status: 'todo',
    createdAt: new Date().toISOString(),
    submittedUserId: ctx.userId,
    assignedUserIds: payload.assigneeIds,
    // A regi, egymezos tarolast is kitoltjuk, hogy a regebbi kliensek is
    // lassak a felelost.
    assignedUserId: payload.assigneeIds[0] ?? null,
    priority: payload.priority,
    deadline: payload.deadline ? payload.deadline.toISOString() : null,
    order: tasks.length,
    followerUserId: payload.followerUserId ?? null,
    fields: (payload.fields ?? []).map((field) => ({
      id: field.id,
      name: field.name,
      type: field.type,
      data: field.value,
      isType: false,
      isOnMainScreen: false,
      status: null,
    })),
  };

  const nextTasks = [...tasks, newTask];

  // A projekt resztvevoi a feladatok feleloseibol allnak ossze — a lista
  // lekerdezes (`userIds` array-contains) erre epul.
  const userIds = new Set<string>(
    Array.isArray(change['userIds']) ? (change['userIds'] as string[]) : [],
  );
  for (const id of payload.assigneeIds) userIds.add(id);
  // A koveto is lassa a projektet, kulonben a `userIds` array-contains
  // lekerdezes miatt a sajat listajan meg sem jelenne a feladat.
  if (payload.followerUserId) userIds.add(payload.followerUserId);
  userIds.add(ctx.userId);

  const newChangeRef = doc(collection(ctx.db, 'projects', ctx.companyId, 'projects', ctx.projectId, 'taskChanges'));

  const batch = writeBatch(ctx.db);

  // 1. a regi verzio mar nem aktualis
  batch.update(taskChangeRef(ctx), { isCurrent: false });

  // 2. az uj verzio a regire mutat vissza
  batch.set(newChangeRef, {
    ...change,
    id: newChangeRef.id,
    previousTaskChangeId: ctx.changeId,
    isCurrent: true,
    companyId: ctx.companyId,
    projectId: ctx.projectId,
    modifierUserId: ctx.userId,
    updatedAt: new Date().toISOString(),
    tasks: nextTasks,
    userIds: [...userIds],
  });

  // 3. a projekt mutatoja az uj verziora all
  batch.update(projectRef(ctx), {
    currentId: newChangeRef.id,
    userIds: [...userIds],
    updatedAtServer: serverTimestamp(),
  });

  await batch.commit();
  return newChangeRef.id;
}

/// A feladat egyedi mezőinek kitöltése (W15).
///
/// Csak a `data` értéket írjuk felül; a mező NEVE, TÍPUSA és a hozzá tartozó
/// státuszfeltétel a sablonból származik, azt a kitöltés nem írhatja át.
export async function saveTaskFields(
  ctx: WriteContext,
  taskId: string,
  values: Record<string, string>,
): Promise<void> {
  await updateTaskRaw(ctx, taskId, (task) => {
    const fields = Array.isArray(task['fields'])
      ? (task['fields'] as Record<string, unknown>[])
      : [];

    task['fields'] = fields.map((field) => {
      const key = typeof field['id'] === 'string' ? field['id'] : String(field['name'] ?? '');
      if (!(key in values)) return field;
      return { ...field, data: values[key] };
    });
  });
}

/// Projektsablon mentése (W22).
///
/// A `taskTypes` tömb első eleme (`order: 0`) a sablon feje: az ő `task`
/// mezője a sablon neve. A mobil oldal is így olvassa, ezért a szerkezet
/// nem térhet el.
export async function saveProjectTemplate(
  db: Firestore,
  companyId: string,
  template: {
    id?: string;
    name: string;
    description?: string;
    tasks: { id?: string; title: string; description?: string }[];
  },
): Promise<string> {
  const ref = template.id
    ? doc(db, 'projects', companyId, 'taskTypes', template.id)
    : doc(collection(db, 'projects', companyId, 'taskTypes'));

  const items = [
    {
      id: crypto.randomUUID(),
      task: template.name,
      desc: template.description ?? '',
      order: 0,
    },
    ...template.tasks.map((task, index) => ({
      id: task.id ?? crypto.randomUUID(),
      task: task.title,
      desc: task.description ?? '',
      order: index + 1,
    })),
  ];

  await setDoc(ref, { id: ref.id, taskTypes: items });
  return ref.id;
}

/// Értesítés olvasottnak jelölése (W33).
///
/// A Flutter a `dateOfSeen` mezőt tölti ki; külön `seen` logikai mező nincs,
/// ezért itt is ugyanazt írjuk.
export async function markNotificationsSeen(
  db: Firestore,
  userId: string,
  ids: string[],
): Promise<void> {
  if (ids.length === 0) return;
  const batch = writeBatch(db);
  const now = new Date().toISOString();
  for (const id of ids) {
    batch.update(doc(db, 'users', userId, 'notifications', id), { dateOfSeen: now });
  }
  await batch.commit();
}

/// Új projekt létrehozása (W19).
///
/// A projekt KÉT dokumentumból áll, és a kettőt egyetlen batch írja:
///
///   projects/{companyId}/projects/{projectId}              - a mutató
///   projects/{companyId}/projects/{projectId}/taskChanges  - a tartalom
///
/// A tartalom (cím, leírás, feladatok) SZÁNDÉKOSAN a `taskChange`
/// dokumentumon él, nem a projekt dokumentumán: a szerkesztés mindig új
/// verziót szül, és a projekt dokumentuma csak az aktuálisra mutat. Ez a
/// Flutter oldal szerződése — a `loadProjects` is innen olvas.
///
/// Az `userIds` MINDKÉT dokumentumra kikerül: a lista lekérdezés a projekt
/// dokumentumán fut (`array-contains`), a részletek viszont a verzióból
/// jönnek. Ha csak az egyikre írnánk, a projekt vagy nem jelenne meg a
/// listán, vagy megjelenne, de üresen.
export async function createProject(
  options: {
    db: Firestore;
    companyId: string;
    userId: string;
  },
  payload: {
    title: string;
    description: string;
    tasks: {
      title: string;
      jobTitle?: string;
      estimateMinutes?: number;
      priority: number;
      assigneeIds?: string[];
    }[];
    /// Ismétlődési szabály RRULE alakban. Ha van, a projekt ismétlődőnek
    /// számít — a `periodicId` a Flutter oldalon ezt jelöli.
    rrule?: string;
  },
): Promise<{ projectId: string; changeId: string }> {
  const { db, companyId, userId } = options;

  const projects = collection(db, 'projects', companyId, 'projects');
  const newProjectRef = doc(projects);
  const changes = collection(
    db,
    'projects',
    companyId,
    'projects',
    newProjectRef.id,
    'taskChanges',
  );
  const newChangeRef = doc(changes);

  const now = new Date().toISOString();

  const userIds = new Set<string>([userId]);
  const tasks: Raw[] = payload.tasks.map((task, index) => {
    for (const id of task.assigneeIds ?? []) userIds.add(id);
    return {
      id: crypto.randomUUID(),
      title: task.title,
      desc: '',
      status: 'todo',
      createdAt: now,
      submittedUserId: userId,
      assignedUserIds: task.assigneeIds ?? [],
      // A regi, egymezos tarolast is kitoltjuk, hogy a regebbi kliensek is
      // lassak a felelost.
      assignedUserId: task.assigneeIds?.[0] ?? null,
      priority: task.priority,
      deadline: null,
      order: index,
      // A munkakor a SABLONBOL jon, es a feladaton marad: ebbol derul ki,
      // kinek kellene elvegeznie, meg akkor is, ha meg nincs felelose.
      jobTitle: task.jobTitle ?? null,
      estimateMinutes: task.estimateMinutes ?? null,
      fields: [],
    };
  });

  const batch = writeBatch(db);

  batch.set(newChangeRef, {
    id: newChangeRef.id,
    companyId,
    projectId: newProjectRef.id,
    // Az ELSO verzio: nincs mire visszamutatnia.
    previousTaskChangeId: null,
    isCurrent: true,
    title: payload.title,
    description: payload.description,
    submittedUserId: userId,
    modifierUserId: userId,
    createdAt: now,
    updatedAt: now,
    tasks,
    userIds: [...userIds],
    ...(payload.rrule ? { rrule: payload.rrule, periodicId: newProjectRef.id } : {}),
  });

  batch.set(newProjectRef, {
    id: newProjectRef.id,
    currentId: newChangeRef.id,
    title: payload.title,
    userIds: [...userIds],
    createdAtServer: serverTimestamp(),
    updatedAtServer: serverTimestamp(),
    // A `taskData` NEM adat, hanem KOMPATIBILITÁSI mező.
    //
    // A mobil modellje kötelezőként olvassa (`json['taskData'] as List`), és
    // ha hiányzik, nem ez az egy kártya marad üresen: az egész feladatlista
    // elhasal, mert a lekérdezés egyben dolgozza fel a cég projektjeit.
    // Emulátoron mérve: a webről felvett projekt után a mobil listája
    // örökre a csontváz-állapotban maradt.
    //
    // Üresen írjuk, mert a feladatok IGAZI helye a `taskChanges` — a mobil
    // maga is felülírja ezt a mezőt onnan, közvetlenül az olvasás után.
    // Kitöltve csak egy elavuló másodpéldányt tartanánk karban.
    taskData: [],
  });

  await batch.commit();
  return { projectId: newProjectRef.id, changeId: newChangeRef.id };
}

/// Ismétlődés beállítása egy MEGLÉVŐ projekten (W21).
///
/// ÚJ VERZIÓT szül, nem helyben ír. Az, hogy egy projekt ismétlődővé válik
/// (vagy megszűnik annak lenni), érdemi változás: a változásnaplóból ki kell
/// derülnie, ki és mikor állította be.
///
/// A `periodicId` a PROJEKT azonosítója — a Flutter `isPeriodic` getter
/// pusztán a mező meglétét nézi. `undefined` szabálynál mindkét mező
/// eltűnik, nem `null` értékkel marad ott.
///
/// Amit ez a mentés NEM csinál: nem hoz létre jövőbeli projekteket. Sem
/// ebben a repóban, sem a cloud functionsben nincs ütemező, ami az `rrule`-t
/// felhasználná — a szabály ma leírás, nem automatizmus. (A `scheduledPushes`
/// kollekció a HATÁRIDŐ-emlékeztetőkről szól, nem az ismétlődésről.)
export async function setProjectRecurrence(
  ctx: WriteContext,
  rrule: string | undefined,
): Promise<string> {
  const change = await loadCurrent(ctx);

  const newChangeRef = doc(
    collection(ctx.db, 'projects', ctx.companyId, 'projects', ctx.projectId, 'taskChanges'),
  );

  const next: DocumentData = {
    ...change,
    id: newChangeRef.id,
    previousTaskChangeId: ctx.changeId,
    isCurrent: true,
    companyId: ctx.companyId,
    projectId: ctx.projectId,
    modifierUserId: ctx.userId,
    updatedAt: new Date().toISOString(),
  };

  if (rrule) {
    next['rrule'] = rrule;
    next['periodicId'] = ctx.projectId;
  } else {
    delete next['rrule'];
    delete next['periodicId'];
  }

  const batch = writeBatch(ctx.db);
  batch.update(taskChangeRef(ctx), { isCurrent: false });
  batch.set(newChangeRef, next);
  batch.update(projectRef(ctx), {
    currentId: newChangeRef.id,
    updatedAtServer: serverTimestamp(),
  });

  await batch.commit();
  return newChangeRef.id;
}
